import { AppRoutingService } from '@core/app-routing.service';
import { ProductService } from '@core/product.service';
import { NavigationMenuItem } from '@models/navigation-menu-item';
import { BehaviorSubject } from 'rxjs';

/**
 * Used to build subnavigation menu for Products Module.
 * Products module will route to either ECE Module, Products Component, and TeacherComponent for Teachers
 * Currently the products subnav is only shown in the Teacher experience.
 *
 * @param {boolean} advancedMenu - Indicates if the product has a more complex subnavigation.
 * @param {BehaviorSubject<NavigationMenuItem[]>} subNavMenuItems
 *  - A BehaviorSubject that holds the sub-navigation menu items.
 * @param {ProductService} productService - The service that provides product-related information.
 * @param {boolean} isHighlightsPortalUser - Indicates if the user is a Highlights Portal user.
 * @param {boolean} showFiveDayPlanner - Indicates if the Five-Day Planner should be shown.
 * @param {boolean} showLessonPage - Indicates if the Lesson Page should be shown.
 */
export function  buildProductsSubnav(
  appRoutingService: AppRoutingService,
  advancedMenu: boolean,
  subNavMenuItems: BehaviorSubject<NavigationMenuItem[]>,
  productService: ProductService,
  isHighlightsPortalUser: boolean,
  showFiveDayPlanner: boolean,
  showLessonPage: boolean,
): void {
  const productLineKey = productService.currentProductLineKey$?.value;
  const variantType = productService.currentVariantType$?.value;
  if (isHighlightsPortalUser) {
    // When ECE, Products routes to ECE Component
    subNavMenuItems.next([
      {
        id: 'ece-planner-sub-menu-item',
        label: 'Planner',
        weight: 0,
        route: appRoutingService.getECEWeeklyPlannerRoute().toString(),
      },
      {
        id: 'ece-materials-sub-menu-item',
        weight: 1,
        route: appRoutingService.getECEMaterialsRoute().toString(),
        label: 'Materials',
      }
    ]);
  } else {
    const newMenus: NavigationMenuItem[] = [];
    // For products that have a more complex subnavigation, such as Superkids and Handwriting 2025.
    // These use the Teacher component.
    // Routing will route to /teacher for Superkids and products with the materials and five-day planner
    if (productLineKey && variantType) {
      newMenus.push(
        {
          id: 'teacher-material-sub-menu-item',
          weight: 1,
          route: appRoutingService.getTeachersMaterialsRoute(
            productService.currentProductLineKey$?.value,
            productService.currentVariantType$?.value,
          ).toString(),
          label: 'Materials'
        },
      );

      if (showLessonPage) {
        newMenus.push(
          {
            id: 'teacher-lesson-sub-menu-item',
            weight: 0,
            route: appRoutingService.getTeachersLessonPageRoute(
              productService.currentProductLineKey$?.value,
              productService.currentVariantType$?.value,
            ).toString(),
            label: 'Lesson Page'
          },
        );
      }

      if (advancedMenu && showFiveDayPlanner) {
        newMenus.push(
          {
            id: 'teacher-planner-sub-menu-item',
            weight: 2,
            route: appRoutingService.getTeachersFiveDayPlannerRoute(
              productService.currentProductLineKey$?.value,
              productService.currentVariantType$?.value,
            ).toString(),
            label: 'Five-Day Planner'
          }
        );
      }
    }

    newMenus.sort((x, y) => x.weight - y.weight);

    subNavMenuItems.next(newMenus);
  }
}
